import { z } from 'zod';
import { BuildingIdSchema, OperationIdSchema } from '../../ids';
import { InventorySchema } from '../../models/inventory';
import { ItemIdSchema } from '../../models/items';
import { API_PREFIX, defineEndpoint } from '../endpoint';

/** The trades of the NPC workers (PROD-WORKER, WK-3). */
export const TradeSchema = z.enum([
  'miner',
  'woodcutter',
  'farmer',
  'laborer',
  // A craft's station's craftsmen (CRAFT-MASTER MC-1, MC-2).
  'smith',
  'carpenter',
  'mason',
  'tanner',
  'weaver',
  'cook',
  'alchemist',
  'jeweler',
  'guard',
  // A ship's hand (MOVE-SHIP CW-2, CW-3).
  'sailor',
]);

/** A hiring hall's id (the layout's `hiringHalls`). */
const HallIdSchema = z.string().min(1).max(64);

/** A worker's id: its entity's. */
export const WorkerIdSchema = z.string().min(1).max(64);

/**
 * An NPC worker as its employer sees it (PROD-WORKER): who it is (its name from its seed), what it
 * does and how well, what it costs, how it feels, and where it works. `arrivesAt` (game time) is
 * when it reaches its post: before it, it is on its way and yields nothing.
 */
export const WorkerViewSchema = z.object({
  id: WorkerIdSchema,
  name: z.string(),
  trade: TradeSchema,
  rank: z.number().int().nonnegative(),
  culture: z.string(),
  /** Its culture is better at its trade (AP-4). */
  trait: z.boolean(),
  /** Coins a game day. */
  wage: z.number().int().nonnegative(),
  morale: z.number().int().nonnegative(),
  /** Until when its wage is paid (game time): its morale moves when the day ends. */
  paidUntil: z.number().int().nonnegative(),
  /**
   * What its day running lacks (its morale falls for each when the day ends): its wage, its food,
   * a bed (null while on its way or without a post: it takes no bed then).
   */
  lacks: z.object({ wage: z.boolean(), food: z.boolean(), bed: z.boolean().nullable() }),
  post: BuildingIdSchema.nullable(),
  arrivesAt: z.number().int().nonnegative(),
  /** It is a master of its trade (CRAFT-MASTER): its station runs a summit material's recipes. */
  master: z.boolean(),
});
export type WorkerView = z.infer<typeof WorkerViewSchema>;

/** A candidate a hall offers today, with the wage it asks now. */
export const CandidateViewSchema = z.object({
  index: z.number().int().nonnegative(),
  name: z.string(),
  trade: TradeSchema,
  rank: z.number().int().nonnegative(),
  culture: z.string(),
  trait: z.boolean(),
  wage: z.number().int().nonnegative(),
});
export type CandidateView = z.infer<typeof CandidateViewSchema>;

/** The character's workers (PROD-WORKER). */
export const getWorkers = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/workers`,
  access: 'session',
  response: z.object({
    serverTime: z.number().int(),
    workers: z.array(WorkerViewSchema),
    /** The character's bad fame as an employer (F4), and what it multiplies the wage asked by. */
    fame: z.number().nonnegative(),
    fameFactor: z.number().min(1),
  }),
});

/**
 * What a hiring hall offers today (WK-4): how many are available of its most, and today's
 * candidates not hired yet, each with the wage it asks (scarcity raises it). `day` names the day
 * a hire must cite.
 */
export const getHall = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/workers/halls/:hall`,
  access: 'session',
  params: z.object({ hall: HallIdSchema }),
  response: z.object({
    hall: HallIdSchema,
    day: z.number().int().nonnegative(),
    available: z.number().int().nonnegative(),
    most: z.number().int().nonnegative(),
    /** Whether the town works for the character at all (its standing with the hall's power, F4). */
    trusted: z.boolean(),
    /** The highest rank it is offered by that standing (null: not trusted). */
    maxRank: z.number().int().nonnegative().nullable(),
    candidates: z.array(CandidateViewSchema),
  }),
});

/**
 * Hires a candidate of today's offer at a hall, standing by its door, for a post of the own site
 * (a structure whose posts take its trade): its first day's wage is paid at once, and it walks to
 * the post (PROD-WORKER technique 6). Idempotent.
 */
export const hireWorker = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/workers/hire`,
  access: 'session',
  body: z.object({
    operationId: OperationIdSchema,
    hall: HallIdSchema,
    /** The day the candidate was offered: another day offers others (`worker_offer_changed`). */
    day: z.number().int().nonnegative(),
    candidate: z.number().int().nonnegative(),
    post: BuildingIdSchema,
  }),
  response: z.object({ serverTime: z.number().int(), worker: WorkerViewSchema }),
});

/** Sends an own worker to another post of a structure of the own site; it walks there. */
export const assignWorker = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/workers/:worker/assign`,
  access: 'session',
  params: z.object({ worker: WorkerIdSchema }),
  body: z.object({ operationId: OperationIdSchema, post: BuildingIdSchema }),
  response: z.object({ serverTime: z.number().int(), worker: WorkerViewSchema }),
});

/**
 * Makes an own craftsman a master of its trade with an ancient crystal from the backpack
 * (CRAFT-MASTER MN-1, MN-2): at a station's post of its trade, of the crystal's least rank, while
 * that site has no master of that trade; the crystal is spent. Idempotent.
 */
export const awakenWorker = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/workers/:worker/awaken`,
  access: 'session',
  params: z.object({ worker: WorkerIdSchema }),
  body: z.object({ operationId: OperationIdSchema, crystal: ItemIdSchema }),
  response: z.object({
    serverTime: z.number().int(),
    worker: WorkerViewSchema,
    /** The backpack without the crystal spent. */
    inventory: InventorySchema,
  }),
});

/** Lets an own worker go: it leaves its post at once and goes home. */
export const dismissWorker = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/workers/:worker/dismiss`,
  access: 'session',
  params: z.object({ worker: WorkerIdSchema }),
  body: z.object({ operationId: OperationIdSchema }),
  response: z.object({ serverTime: z.number().int() }),
});
